export const en_GB = {
  translation: {
    mfcTitle: "Motor Finance Commission Scheme",
    landingPage: {
      subtitle: "Enter your case reference from your letter or email, and date of birth.",
      moreThanOneAgreement: "If you have more than one agreement with us you’ll need to sign in separately for each one.",
      referenceNumber: "Case reference",
      findReferenceNumber: "Find your case reference",
      findRefParagraph1: "Your case reference is shown in the side panel of your letter, above the QR code.",
      findRefParagraph2: "If you received an email, it’s above the link you clicked to get here.",
      findRefParagraph3: "Don’t have your letter or email? <faqLink>Visit our FAQs or contact us.</faqLink>",
      customerRadioLabel: "Are you a commercial customer?",
      personalCustomerRadio: "No, I’m a personal customer",
      commercialCustomerRadio: "I’m a commercial customer (a sole trader or partnership)",
      continue: "Continue",
      continueToChallenge: "Continue to challenge",
      acceptAndSubmit: "Accept and submit",
      aboutScheme: "About the Scheme",
      schemeInfo1: "The FCA has set up the Scheme to look at how commission paid on motor finance agreements was handled in the past.",
      schemeInfo2: "It covers agreements taken out between 6 April 2007 and 1 November 2024 where certain commission arrangements applied, which may not have been adequately disclosed.",
      schemeInfo3: "For more information about the Scheme, or for ways to contact us, <faqLink>go to our FAQs</faqLink>.",
      errors: {
        referenceNumberRequired: "Reference number is required",
      }
    },
    personalDetails: {
      dobHelperText: "The agreement holder’s date of birth. For example, 31 12 1982.",
      labels: {
        dobDay: "Day",
        dobMonth: "Month",
        dobYear: "Year",
        dobCombined: "Date of birth",
      },
      error: {
        dobRequired: "Date of birth is required",
        dobMissingFields: "Date of birth must include a {{fields}}",
        dobDayInvalid: "Day must be between 1 and 31",
        dobMonthInvalid: "Month must be between 1 and 12",
        dobYearInvalid: "Year must be valid",
        dobInvalidDate: "Date of birth must be a real date",
        dobInFuture: "Date of birth must be in the past",
        dobMinAge: "You must be at least 18 years old",
      },
    },
    caseLanding: {
      subtitle: "For more information about the Scheme, read your letter or email, or visit our <faqLink>FAQs</faqLink>.",
      submit: "Submit",
      submitAgree: "Accept and submit",
      submitChallenge: "Continue to challenge"
    },
    confirmChallenge: {
      nextStepsTitle: "What would you like to do next?",
      acceptRadioLabel: "Accept decision",
      acceptTitle: "Accept the redress decision",
      acceptDesc: "Accept the decision set out in your letter or email.",
      disagreeRadioLabel: "Challenge the decision",
      disagreeTitle: "Disagree with the redress outcome",
      disagreeDesc: "If you think the outcome is incorrect or we may be missing information, you can challenge our decision.",
      step1Title: "You’re about to notify us that you want to challenge our decision",
      step1Para1: "Please check your details before notifying us of your challenge.",
      step1Para2: "Updating your contact details will change them across all of your Motor Finance Commission Scheme cases.",
      step1Para3: "If you have a current finance agreement with us, changing your address, email address or phone number in this portal won't update the details linked to your agreement. Please <faqLink>contact us as soon as possible</faqLink> to update these details too.",
      continueToChallenge: "Continue to your challenge",
      step2Title: "Challenging our decision",
      step2Para1: "You disagree with our decision and want us to review it.",
      whatNext: "What happens next?",
      whatNextBullets: [
        "1. We’ll record your challenge",
        "2. We’ll send you a letter or email explaining how to continue with your challenge and provide any evidence that may be useful.",
      ],
      whatNextUploadInfo: "Uploading supporting information helps us review your challenge as fully as possible.",
      confirmChallenge: "Confirm your challenge",
      challengeTitle: "I want to challenge this decision",
      challengeRadio: "I disagree with the decision and want it to be reviewed",
      noChallengeTitle: "I don’t want to challenge this decision",
      noChallengeRadio: "I’ve read the information and don’t want to challenge the decision at this stage",
      submitChallenge: "Submit your challenge"
    },
    formalObjectionEntry: {
      title: "Do you want to submit your formal objection now?",
      para1: "As you‘ve already told us you want to challenge your redress decision, we’re sending you information about it by email or post.",
      para2: "You can explain your reasons and upload supporting evidence.",
      para3: "If you’re ready, you can submit your objection now.",
      startObjection: "Start objection"
    },
    challenge: {
      subtitle:
        "For more information about the Scheme, read your letter or email, or visit our FAQs.",
      stepCounter: "Step {{current}} of {{total}}",
      steps: {
        contactDetails: {
          title: "You’re about to submit your objection",
          checkDetails: "Please check your details and tell us why you’re challenging the decision.",
          updateAll: "Updating your contact details will change them across all of your Motor Finance Commission Scheme cases.",
          currentAgreement: "If you have a current finance agreement with us, changing your address, email address or phone number in this portal won't update the details linked to your agreement. Please <faqLink>contact us as soon as possible</faqLink> to update these details too.",
          reviewYourChallenge: "We’ll review your challenge and respond within the timeframes set by the Financial Conduct Authority."
        },
        challengeReason: {
          title: "Tell us why you’re challenging this decision",
        },
        documentUpload: {
          title: "Optional: Upload documents to support your challenge",
        },
        confirmDocuments: {
          title: "Select a document type for each file before submitting your objection",
        },
        importantInformation: {
          title: "Important information",
        },
      },
      actions: {
        continueWithoutEvidence: "Continue without evidence",
        continueToReview: "Continue to review",
        submitFormalObjection: "Submit your objection",
      },
    },
    challengeReason: {
      label: "Reason for challenge",
      placeholder: "Select a reason",
      explainYourChallenge: "Explain your challenge",
      explainYourChallengeText:
        "Please explain why you think this decision is incorrect. Include as much detail as possible, such as dates, amounts, or documents.",
      explainYourChallengeHelperText: "If you need more room to explain your challenge, you can attach a document with further details in the next step.",

      errors: {
        required: "Please select a reason",
        challengeExplanation: "Please explain your challenge",
      },
      header: {
        title: "Motor Finance Commission Scheme",
        support:
          "For more information about the Scheme, read your letter or email, or visit our <faqLink>FAQs</faqLink>.",
        step: "Step 2 of 4",
      },

      intro: {
        choose: "Choose the main reason you think our decision is incorrect.",
        explainLater:
          "You'll be able to explain your challenge in more detail below and upload supporting information in the next step.",
      },

      moreInfo: {
        title: "More information about this reason",
        placeholder: "Select a reason to see more information and examples.",
        reasons: {
          agreementDetailsWrong: {
            intro: "You believe the details we have for your agreement are wrong. This could include:",
            bullets: ["the amount you borrowed", "the dates your agreement started or ended", "the APR"],
          },
          commissionArrangementOrInterestRateAssessment: {
            intro: "Use this option if your concern is about the agreement itself.",
          },
          anotherReason: {
            intro: "You disagree or have more information or evidence for us to consider.",
          },
        },
      },

      options: {
        agreementDetailsWrong: "My agreement details are wrong",
        commissionArrangementOrInterestRateAssessment: "I believe my agreement was unfair",
        anotherReason: "I have another reason for challenging this decision",
      },
    },
    confirmAcceptanceModal: {
      title: "Confirm your acceptance of this decision",
      paragraphs: [
        "If you agree with the outcome of our review, you can accept this decision. This will be in full and final settlement of your claim with us, including any legal claims. As a result, the <strong>Financial Ombudsman Service or a court is unlikely to consider any further claim about the commission on this agreement.</strong>",
        "This only applies to this one agreement. If you have other agreements, you’ll be able to manage them individually."
      ],
      confirm: "I accept the decision",
      cancel: "Go back"
    },
    "dontWantToChallenge": {
      "title": "Don’t want to challenge at this stage?",
      "comingBackLaterTitle": "Challenge later",
      "comingBackLaterDescription": "You can return later to challenge our decision, as long as you are still within the time limits explained in your letter or email.",
      "acceptingRedressTitle": "Accept the decision",
      "acceptingRedressDescription": "If you agree with our decision, you can return to your case and accept the decision.",
      "returnToCaseButton": "Return to your case"
    },


    "confirmDocuments": {
      "documentTypeLabel": "Document type",
      "subtitleOne": "Select a document type for each file to help us review your objection.",
      "subtitleTwo": "Your documents will be securely stored and only used to review your objection.",
      "removeDocument": "Remove document",
      "noDocuments": "No documents have been uploaded yet.",
      "goBackToUpload": "Go back to the previous step to upload documents.",
      "importantInformationTitle": "Important information",
      "importantInformationBody": "Please check that each uploaded document has the correct document type selected before submitting.",
      "noDocumentInfo": "You’re about to submit your challenge without attaching any documents. If you have documents that may help us review your request, you can go back and upload them before continuing.",
      "errors": {
        "required": "Please upload at least one document.",
        "documentTypeRequired": "Please select a document type."
      },
      "options": {
        "financeContract": "Finance agreement",
        "StatementOfAccount": "Statement of account",
        "documentFromDealer": "Document from dealer",
        "somethingElse": "Other"
      }
    },

    optInOut: {
      title: "Do you want us to assess your case under the Scheme?",
      optInTitle: "Yes, assess my case",
      optInPara1: "We’ll assess your case and, if you are owed redress, arrange payment.",
      optInPara2: "You’ll also be able to manage your details through our secure portal.",
      optInRadio: "Yes, continue with my assessment",
      optOutTitle: "No, I don’t want my case assessed",
      optOutPara1: "If you choose not to continue, we won’t assess your case or arrange any redress payment at this stage.",
      optOutPara2: "You can return and change your mind within 6 months of the date of your letter or email.",
      optOutRadio: "No, I don’t want my case assessed"
    },
    confirmLeaveSchemeModal: {
      title: "Are you sure you want to opt out of the Scheme?",
      paragraphs: [
        "If you choose to opt out of the Scheme, no further action will be taken on this agreement and we’ll close your case.",
        "Your choice to opt out only applies to this agreement.",
      ],
      confirm: "Yes, opt out",
      cancel: "Go back",
    },
    contactDetails: {
      title: "Your details",
      contactInfo: "Contact information",
      yourDetails: "Your details",
      name: "Name",
      email: "Email",
      phoneNumber: "Phone number",
      address: "Address",
      updateContactDetails: "Update contact details",
      incompleteTitle: "Your contact details are incomplete",
      incompleteBody: "Please update your contact details to add the missing information.",
      updateAllInfo: "Updating your contact details will change them across all of your Motor Finance Commission Scheme cases.",
      updateAllCasesNotice: "If you have a current finance agreement with us, changing your address, email address or phone number in this portal won't update the details linked to your agreement. Please <faqLink>contact us as soon as possible</faqLink> to update these details too."
    },
    editContactDetails: {
      name: "Name",
      updateName: "To update your name, please <contactLink>visit our FAQs</contactLink> for ways to contact us.",
      email: "Email",
      phoneNumber: "Phone number",
      address: "Address",
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "Town/City",
      county: "County (optional)",
      postcode: "Postcode",
      country: "Country",
      enterPostcode: "Enter your postcode",
      selectAddress: "Select your address",
      enterManually: "Enter address manually",
      backToLookup: "← Back to address search",
      changeAddress: "← Change address",
      nonUkTitle: "Non-UK addresses",
      nonUkInfo: "If you have a non-UK address, please <faqLink>visit our FAQs</contactLink> for ways to contact us.",
      updateDetails: "Update details",
      cancel: "Cancel",
      errors: {
        emailRequired: "Please enter a valid email address",
        phoneNumberRequired: "Please enter a valid phone number",
        address1Required: "Please enter a valid address line 1",
        address2Valid: "Please enter a valid address line 2",
        cityRequired: "Please enter a valid town/city",
        postcodeRequired: "Please enter a valid postcode",
        addressRequired: "Please enter your current address using the postcode search or manual entry. Contact us if you need any help.",
        postcodeError: "Something went wrong. Please try again or enter your address manually",
        checkDetails: "Please check your details are correct."
      }
    },
    bankDetailsValidated: {
      title: "Bank details",
      para1: "You’ve already given us your bank details.",
      para2: "If you need to update them, please <faqLink>contact us through our FAQs</faqLink>."
    },
    bankDetailsForm: {
      step1Title: "You’re about to accept the redress decision",
      step1Subtitle: "Please check your contact details before continuing to payment details.",
      continueToBank: "Continue to payment details",
      title: "Your payment details",
      subtitle: "Please provide your bank details as they appear on your bank account so we know where to send the payment. Your details will only be used to arrange this payment.",
      payeeName: "Payee name (as it is on the bank account)",
      sortCode: "Sort code",
      sortCodeHint: "Enter a 6 digit sort code",
      validSortCode: "Valid sort code",
      accountNumber: "Account number",
      validAccountNumber: "Valid account number",
      payingAnotherWay: "More about payments",
      nonUkBankAccountsTitle: "Non-UK bank accounts",
      nonUkBankAccountsBody: "If you don’t have a UK bank account, please <faqLink>contact us through our FAQs</faqLink> so we can arrange payment another way.",
      alternativePaymentTitle: "Alternative payment methods or payment on behalf of someone else",
      alternativePaymentBody: "To receive payment using an alternative payment method, or to arrange payment on behalf of somebody else, please <faqLink>contact us through our FAQs</faqLink>.",
      submitBankDetails: "Accept and submit bank details",
      validation: {
        payeeNameRequired: "Please enter a valid payee name",
        sortCodeRequired: "Please enter a valid 6 digit sort code",
        accountNumberRequired: "Please enter a valid account number",
      }
    },
    bankDetailsLoading: {
      title: "Verifying your bank details",
      subtitle: "We’re securely checking your details. This should only take a moment."
    },
    loadingScreen: {
      title: "Submitting your request",
      subtitle: "We’re securely processing your request. This should only take a moment."
    },
    contactInfo: {
      title: "How we’ll contact you",
      genericParas: [
        "We’ll contact you to keep you updated about your case.",
        "If you have a preference on how you receive updates, you can update your contact details."
      ],
      emailParas: [
        "We’ll contact you by email to keep you updated about your case.",
        "Please check your email address is correct so you don’t miss updates.",
        "If you’d prefer to receive updates by post, you can update your contact details. Post may take longer to arrive."
      ],
      postParas: [
        "We’ll contact you by post to keep you updated about your case.",
        "Post may take longer to arrive.",
        "If you’d prefer updates by email instead, you can update your contact details."
      ],
      preferenceTitle: "How would you like to receive updates?",
      preferencePara1: "We recommend email as the quickest way to receive updates about your case.",
      preferencePara2: "You can choose post instead, but letters may take longer to arrive.",
      emailRadio: "Email (usually faster)",
      postRadio: "Post",
      contactPreferenceRequired: "Please select your contact preference",
      subHeading: "Contact preference"
    },
    importantInformation: {
      title: "Important information",
      aboutScheme: "Privacy Policy",
      para1: "Having your case assessed doesn’t guarantee that you’ll receive a redress payment.",
      docChallengeInfo: "If you challenge more than one agreement, we’ll review each one and send you a separate email or letter.",
      para2: "This Motor Finance Commission Scheme website is provided by Ford Credit (a trading style of FCE Bank plc).",
      para3: "By continuing, you agree to our <privacyPolicyLink>Privacy Policy</privacyPolicyLink>.",
      para4: "This is not marketing consent.",
      agreeCheckbox: "I agree to the Privacy Policy."
    },
    receivedLetter: {
      title: "Please confirm you’ve received and read your letter or email",
      complaintTitle: "Have you received and read your acknowledgement letter or email?",
      receivedTitle: "Yes, I’ve received and read it",
      receivedSubtitle: "Choose this if you’ve read the information we sent you about our decision and the options available to you.",
      receivedComplaintSubtitle: "Choose this if you’ve read the information we sent about submitting your objection.",
      receivedRadio: "I’ve received and read the letter or email",
      notReceivedTitle: "No, I haven’t read it yet",
      notReceivedComplaintTitle: "No, I haven’t read it",
      notReceivedRadio: "I haven’t yet received or read the letter or email",
      infoTitle: "You’ll need to read your letter or email before continuing",
      infoParas: [
        "Your letter or email explains our decision and the options available to you.",
        "Once you’ve received and read it, you can return here to continue.",
      ],
      complaintInfoTitle: "You can continue once you receive your acknowledgement letter or email",
      complaintInfoParas: [
        "We’ve sent you a letter or email explaining how to submit your objection and supporting information.",
        "Once you’ve received and read it, you can return here to continue.",
        "If you haven’t received your acknowledgement after 7 days, please <contactLink>contact us</contactLink>."
      ]
    },
    challengeDecision: {
      title: "Would you like to challenge this decision?",
      info: "If you believe this decision is incorrect, or that we may be missing information about your motor finance, you have the right to challenge this decision.",
      challengeDecision: "Challenge this decision"
    },
    howToProceed: {
      title: "What would you like to do next?",
      acceptTitle: "Accept the decision",
      acceptPara: "The details are set out in your letter or email.",
      acceptRadio: "Accept the decision",
      acceptCantChallenge: "Please read the information in your email or letter before accepting.",
      challengeTitle: "Challenge the decision",
      challengePara: "If you think the outcome is incorrect or we may be missing information, you can challenge our decision.",
      challengePara1: "We’ll review your case again, taking into account any new information you provide.",
      challengeRadio: "Challenge decision",
      cantChallengeTitle: "Would you like to accept the decision?",
      yesAcceptBtn: "Yes, accept the decision",
    },
    thankYouPage: {
      optedIn: {
        title: "You’re opted in",
        subtitle: "We’ll assess your case under the Motor Finance Commission Scheme.",
        infoTitle: "What happens next?",
        infoPara1: "We’ll begin the next stage of your assessment. We’ll then contact you as soon as this is complete to let you know the outcome and if any compensation is due. You don’t need to do anything else right now. ",
        infoPara2: "Our response times follow timeframes set by the Financial Conduct Authority under this Scheme, which means they may be longer than usual."
      },
      optedOut: {
        title: "You’ve left the Scheme",
        subtitle: "We’ll close your case under the Motor Finance Commission Scheme.",
        infoTitle: "What does this mean?",
        infoPara1: "You’ve chosen to opt out of the Scheme. We’ll close your case and won’t take any further action under the Scheme in relation to this agreement.",
        infoPara2: "Your choice to opt out only applies to this agreement."
      },
      documentsSubmitted: {
        title: "Documents submitted",
        subtitle: "We’ve received your documents and will reassess your complaint under the Motor Finance Commission Redress Scheme.",
        infoTitle: "What happens next?",
        infoPara1: "We will acknowledge your challenge within 7 days. We’ll review the documents you’ve submitted alongside your existing records.",
        infoPara2: "You don’t need to do anything else right now. Once we have reassessed your complaint we will confirm our final position and explain whether our decision has changed.",
        infoPara3: "We are following FCA rules for motor finance. This means our response times may be longer than usual."
      },
      contactDetailsUpdatedEmail: {
        title: "Contact details updated",
        subtitle: "We’ve updated your contact details.",
        infoTitle: "How we’ll contact you",
        infoPara1: "We’ll contact you by <strong>email</strong> with updates about your case.",
        infoPara2: "Please check your email address is correct so you don’t miss updates."
      },
      contactDetailsUpdatedPost: {
        title: "Contact details updated",
        subtitle: "We’ve updated your contact details.",
        infoTitle: "How we’ll contact you",
        infoPara1: "We’ll contact you by <strong>post</strong> with updates about your case.",
        infoPara2: "Please check your address is correct so you don’t miss updates."
      },
      challengeSubmitted: {
        title: "Challenge submitted",
        subtitle: "We’ve received your notification of challenge.",
        infoTitle: "What happens next?",
        infoPara1: "We’ll acknowledge your challenge within 7 days by letter or email.",
        infoPara2: "Your letter or email will explain how to continue with your challenge and provide any evidence that may be useful."
      },
      objectionSubmitted: {
        title: "Objection submitted",
        subtitle: "We’ve received your objection and any documents you uploaded.",
        infoTitle: "What happens next?",
        infoPara1: "We’ll review your objection and any supporting information before sending you our decision.",
        infoPara2: "We’ll contact you with our decision within 2 months.",
        infoPara3: "You don’t need to do anything else right now."
      },
      noRedressAccepted: {
        title: "Decision accepted",
        subtitle: "You have accepted the decision we made about your case.",
        infoTitle: "What happens next?",
        infoPara1: "You don’t need to do anything else for this agreement. We’ll send you an acknowledgement of your acceptance by letter or email.",
        infoPara2: "This only applies to this one agreement. If you have other agreements, you’ll be able to manage them individually."
      },
      redressAccepted: {
        title: "Decision accepted",
        subtitle: "We’ve received your bank details.",
        infoTitle: "What happens next?",
        infoPara1: "Your bank details have been received.",
        infoPara2: "We’ll use these details to arrange payment. We’ll contact you if we need anything further."
      },
      "bankDetailsNameValidationFailed": {
        "title": "Please give us a call to continue",
        "subtitle": "We need to verify your information before we can authorise your payment.",
        "infoTitle": "What does this mean?",
        "infoPara1": "We couldn’t verify your information online.",
        "infoPara2": "To make sure your payment is secure, you’ll need to verify your information with one of our customer support team.",
        "infoPara3": "<strong>You haven’t accepted the redress decision or compensation yet.</strong>",
        "callButton": "Give us a call",
        "emailButton": "Email us",
      },
      backToDetails: "Back to your case",
    },
    questionsAboutScheme: {
      title: "Questions about the Scheme?",
      infoSupportTitle: "Need more information about our decision?",
      questionsInfo: "For more information about the Scheme, or to contact us, go to our FAQs.",
      infoSupportInfo: "For more information about the Scheme or to contact us about our decision, go to our FAQs.",
      goToFaqs: "Go to FAQs"
    },
    optOut: {
      title: "Want to leave the Scheme?",
      para: "If you leave the Scheme, we’ll stop assessing this case and close it.",
      optOutScheme: "Leave the Scheme"
    },
    unableToChallenge: {
      title: "Contact the Financial Ombudsman Service to challenge the decision",
      infoPara1: "We cannot review challenges for this case through the Motor Finance Commission Redress Scheme.",
      infoPara2: "You can still refer your complaint to the Financial Ombudsman Service (FOS).",
      subTitle: "Financial Ombudsman Service (FOS)",
      infoPara3: "If you’re unhappy with our decision, you can contact the Financial Ombudsman Service free of charge.",
      infoPara4: "You must contact them within 6 months of the date of your email or letter.",
      buttonText: "Go to the FOS website",
    },
    documentUpload: {
      title: "If you have documents you’d like us to consider, you can upload them below.",

      examplesTitle: "You can upload documents such as:",
      examples: {
        financeAgreements: 'Finance agreements or "welcome packs"',
        initialDisclosure:
          "Pre-contract information from the dealer or broker including Initial Disclosure Documents (IDD)",
      },
      storageStatement: "Your documents will be securely stored and only used to review your objection.",

      helperText:
        "You can upload JPG, JPEG, PNG, HEIC, HEIF, WEBP, BMP or PDF files. Files must be {{maxFileSizeMb}} MB or smaller. You can attach up to {{count}} files.",

      browse: "Browse",
      submit: "Upload docs",
      submitting: "Submitting",
      clear: "Clear",

      chooseAtLeastOneFile: "Please choose at least one file first.",
      convertingProgress: "Converting {{current}} of {{total}}: {{fileName}}",
      conversionComplete: "Conversion complete.",

      convertedPdfs: "Converted PDFs",
      from: "From: {{sourceName}}",

      conversionFailed: "Conversion failed",

      download: "Download",
      downloadAll: "Download all",
      errors: {
        chooseAtLeastOneFile: "Please choose at least one file first.",
      }
    },
    pdfConversion: {
      errors: {
        canvasNotSupported: "Canvas not supported in this browser.",
        failedToConvertImageToPng: "Failed to convert image to PNG.",
        canvasContextNotAvailable: "Canvas context not available.",
        failedToEncodeCanvas: "Failed to encode canvas.",
        officeExtensionNotConvertibleFrontend:
          "{{ext}} cannot be reliably converted to a readable PDF purely in the browser. Without a backend, you’ll need to restrict uploads or accept a fallback PDF that just contains the original file as an attachment.",
      },
      pdf: {
        title: "Document packaged as PDF",

        lines: {
          originalFilename: "Original filename: {{fileName}}",
          originalMimeType: "Original MIME type: {{mimeType}}",
          originalSizeBytes: "Original size: {{size}} bytes",
          createdInBrowser: "This PDF was created in the browser.",
          officeRequiresBackend:
            "For Office documents, true rendering to PDF pages typically requires a backend converter.",
          attachmentEmbedded: "Original file is embedded as an attachment in this PDF.",
          attachmentsNotSupported: "Note: This build of pdf-lib does not support attachments.",
        },

        attachment: {
          description: "Original file attached",
        },
      },
    },
    caseInfoBanner: {
      beforeYouContinue: {
        infoTitle: "Before you continue",
        infoPara1: "We’ve sent you a letter or email about the <strong>Motor Finance Commission Scheme</strong>.",
        infoPara2: "It explains:",
        infoBulletList2: [
          "how the Scheme relates to your agreement",
          "your available options",
          "important information you should read before continuing"
        ]
      },
      inProgress: {
        infoTitle: "We’re reviewing your agreement",
        infoPara1: "We’re reviewing your agreement and assessing whether any redress is due.",
        infoPara2: "You don’t need to do anything right now. We’ll contact you when your assessment is complete.",
        infoPara3: "Our response times follow timeframes set by the <strong>Financial Conduct Authority</strong>, which may mean they are longer than usual."
      },
      notIncluded: {
        infoTitle: "We are not currently assessing your case",
        infoPara1: "You previously chose not to have your case assessed under the Motor Finance Commission Redress Scheme.",
        infoPara2: "You don’t need to do anything unless you want us to assess your case.",
        infoPara3: "You can still choose to have your case assessed within 6 months of the date in your letter or email."
      },
      optedOut: {
        infoTitle: "You’ve left the Scheme",
        infoPara1: "You’ve chosen to opt out of the Scheme. We’ll close your case and won’t take any further action under the Scheme in relation to this agreement.",
        infoPara2: "Your choice to opt out only applies to this agreement.",
      },
      unknown: {
        infoTitle: "We don’t have any updates on your case right now",
        infoPara1: "We’re unable to display any further details for this case at this time.",
        infoPara2: "If you have any questions about your case, or need to speak to us, please contact us."
      },
      cancelled: {
        infoTitle: "We don’t have any updates on your case right now",
        infoPara1: "We’re unable to display any further details for this case at this time.",
        infoPara2: "If you have any questions about your case, or need to speak to us, please contact us."
      },
      updateSent: {
        infoTitle: "We’ve sent you an update",
        infoPara1: "We’ve sent you an email or letter with the latest information about your case.",
        infoPara2: "Please read the information for details.",
        infoPara3: "If we’ve sent you a letter, it can take up to 2 weeks to arrive."
      },
      tryAgainLater: {
        infoTitle: "Please try again later",
        infoPara1: "We’ve had to disable some features and have toggled off all the interactive functionality except for updating contact details."
      },
      decisionIssued: {
        infoTitle: "We’ve reviewed your agreement and sent you our decision",
        infoPara1: "We’ve checked this agreement against the Scheme rules.",
        infoPara2: "<strong>We’ve sent our decision to you by letter or email.</strong>",
        infoPara3: "<strong>What do I need to do?</strong>",
        infoPara4: "Read the letter or email we’ve sent you. It explains:",
        infoBulletList4: [
          "the Scheme",
          "how we reached our decision",
          "what happens next",
        ],
      },
      caseOnHold: {
        infoTitle: "Your case is on hold",
        infoPara1: "You don’t need to do anything right now. We’ll contact you if we need any further information.",
        infoPara2: "Our response times follow timeframes set by the <strong>Financial Conduct Authority</strong>, which may mean they are longer than usual."
      },
      caseClosed: {
        infoTitle: "This case has been closed",
        infoPara1: "We’ve checked this agreement against the Scheme rules.",
        infoPara2: "We’ve sent our decision to you by letter or email, and closed your case.",
        infoPara3: "You don’t need to do anything else."
      },
      noRedressCaseClosed: {
        infoTitle: "This case has been closed",
        infoPara1: "We’ve checked this agreement against the Scheme rules.",
        infoPara2: "<strong>We’ve sent our decision to you by letter or email, and closed your case.</strong>",
        infoPara3: "You don’t need to do anything else right now.",
      },
      startFormalObjection: {
        infoTitle: "Start your objection",
        infoPara1: "You’ve told us you want to challenge our decision.",
        infoPara2: "<strong>We’ve sent you an acknowledgement by letter or email explaining how to submit your objection for this agreement.</strong>",
        infoPara3: "Please read the acknowledgement carefully before continuing."
      },
      objectionReview: {
        infoTitle: "Your objection is being reviewed",
        infoPara1: "We’ve received your objection and are reviewing it alongside the information you provided and our existing records.",
        infoPara2: "You don’t need to do anything else right now. Once we’ve completed our review, we’ll contact you with our decision.",
      },
      arrangingPayment: {
        infoTitle: "We’re arranging your payment",
        infoPara1: "You’ve <strong>accepted</strong> the redress (compensation) for your motor finance agreement based on the criteria set out by the Financial Conduct Authority.",
        infoPara2: "<strong>Your bank details have been received.</strong>",
        infoPara3: "We’ve received the bank details you provided and will use them to arrange your redress payment.",
        infoPara4: "You don’t need to do anything else right now. We’ll contact you if we need any further information."
      },
    },
    maintenance: {
      title: "The Motor Finance Commission Scheme portal is currently unavailable",
      subtitle: "We’re carrying out routine maintenance and can’t sign you in right now.",
      contactHeader: "Questions about the Scheme?",
      contactText: "For more information about the Scheme, or to contact us, go to our FAQs.",
      contactButton: "Go to FAQs"
    },
    common: {
      continue: "Continue",
      submit: "Submit",
      submitting: "Submitting ...",
      goBack: "Go back"
    },
    header: {
      signOut: "Sign out"
    },
    errors: {
      genericErrorBanner: {
        title: "Something went wrong",
        couldntSaveBankDetails: "We couldn’t save your bank details",
        body: "Please try again later, and if you need help in the meantime, <contactLink>contact us through our FAQs</contactLink>.",
        docUploadBody: "Please try again. If this continues, <contactLink>go to our FAQs</contactLink> for ways to contact us."
      },
      fileType: {
        title: "You can only upload JPG, JPEG, PNG, HEIC, HEIF, WEBP, BMP or PDF files. Files were not uploaded because '{{fileName}}' is not a supported file type.",
        body: "If you need to provide additional evidence, please <faqLink>go to our FAQs</faqLink> or contact us.",
      },
      fileSize: {
        title: "You can only upload files up to {{maxFileSizeMb}} MB, The file '{{fileName}}' was too large and was not uploaded.",
        body: "If you need to provide additional evidence, please <faqLink>go to our FAQs</faqLink> or contact us."
      },
      numOfFiles: {
        title: "You can only upload {{maxFilesCount}} files, please remove {{excessFiles}} file(s).",
        body: "If you need to provide additional evidence, please <faqLink>go to our FAQs</faqLink> or contact us."
      },
      signInGeneric: {
        title: "We can’t sign you in right now",
        body: "Please come back later and try again, or <faqLink>visit our FAQs</faqLink> if you need support in the meantime."
      },
      signInFailed: {
        title: "We couldn’t sign you in with those details",
        body_one: "Please check your case reference and date of birth, then try again. <br/>If you need support, please <faqLink>visit our FAQs.</faqLink> for ways to contact us.",
        body_other: "Please check your case reference and date of birth, then try again. <br/>If you need support, please <faqLink>visit our FAQs.</faqLink> for ways to contact us.",
      },
      lastAttempt: {
        title: "Check your details carefully",
        body: "You have 1 attempt remaining. <br/>If you need support, please <faqLink>visit our FAQs.</faqLink> for ways to contact us before trying again.",
      },
      refLock: {
        title: "Your account is locked",
        body: "For your security, access to this case has been locked because there have been too many unsuccessful login attempts. <br/>To unlock your account, please <faqLink>visit our FAQs.</faqLink> for ways to contact us.",
      },
      sessionLock: {
        title: "You’ve used all your attempts",
        body: "If you need help signing in to the portal, <faqLink>visit our FAQs.</faqLink> for ways to contact us.",
      },
      updateGeneric: {
        title: "Something went wrong",
        body: "Please try again. If this continues, <faqLink>go to our FAQs</faqLink> for ways to contact us."
      }
    },
    links: {
      faqLink: "https://www.ford.co.uk/support/how-tos/owner-resources/ford-credit/motor-finance-commission-complaints",
      contactLink: "https://www.ford.co.uk/support/how-tos/owner-resources/ford-credit/motor-finance-commission-complaints",
      tandcsLink: "https://www.ford.co.uk/useful-information/terms-and-privacy",
      privacyPolicyLink: "https://www.ford.co.uk/useful-information/terms-and-privacy",
      fos:"https://www.financial-ombudsman.org.uk/"
    },
    footerCopyright: "\u00A9 {{year}} Ford Motor Company",
    footerList: [
      {
        url: "https://www.ford.co.uk/support/how-tos/owner-resources/ford-credit/motor-finance-commission-complaints",
        linkText: "FAQs"
      },
      {
        url: "https://www.ford.co.uk/useful-information/accessibility",
        linkText: "Accessibility"
      },
      {
        url: "https://www.ford.co.uk/useful-information/terms-and-privacy/cookie-preferences",
        linkText: "Cookie Preferences"
      },
      {
        url: "https://www.ford.co.uk/useful-information/legal-notices",
        linkText: "Ford Legal Notices"
      },
      {
        url: "https://www.ford.co.uk/useful-information/terms-and-privacy",
        linkText: "Privacy Policy"
      }
    ]
  },
};
